import { useState, useEffect, useCallback, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import Swal from "sweetalert2";
import { debounce } from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Loader from "../../../components/Loader/Loader";
import { Link } from "react-router-dom";
import "../../../components/common/css/datatable.scss";
import { Button } from "@mui/material";

const InvestmentList = () => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageState, setPageState] = useState({
    data: [],
    total: 0,
    page: 1,
    pageSize: 9,
  });
  const [selectedUnpaid, setSelectedUnpaid] = useState([]);
  const [achFormat, setAchFormat] = useState("RBC_ACH");
  const [achFileUrl, setAchFileUrl] = useState(null);
  const [showValidationOptions, setShowValidationOptions] = useState(false);
  const [selectedValidationOption, setSelectedValidationOption] = useState("");
  const fileInputRef = useRef();

  const fetchInvestData = () => {
    const token = localStorage.getItem("accessToken");
    setLoading(true);
    setPageState((old) => ({ ...old }));
    var config = {
      method: "post",
      url: process.env.REACT_APP_API + "admin/get-campaign-invest-listing",
      headers: {
        "x-access-token": token,
      },
      data: {
        page: pageState.page,
        limit: pageState.pageSize,
        searchTerm: query,
      },
    };

    axios(config)
      .then(function (response) {
        setPageState((old) => ({
          ...old,
          data: response?.data?.data?.rows,
          total: response?.data?.data?.count,
        }));
        setLoading(false);
      })
      .catch(function (error) {
        setLoading(false);
        Swal.fire({
          title: error.response.data.message,
          timerProgressBar: true,
          icon: "error",
          confirmButtonText: "OK",
          showCloseButton: true,
        });
      });
  };

  useEffect(() => {
    fetchInvestData();
  }, [pageState.page, pageState.pageSize, query]);

  // Cleanup function to revoke blob URLs and clear stored data when component unmounts
  useEffect(() => {
    return () => {
      // Revoke blob URL if it exists
      if (achFileUrl && achFileUrl.startsWith("blob:")) {
        URL.revokeObjectURL(achFileUrl);
      }
    };
  }, [achFileUrl]);

  const handleQuery = useCallback(
    debounce((e) => {
      setQuery(e.target.value);
      setPageState({ ...pageState, page: 1 });
    }, 1000)
  );

  const handleGenerateACH = async () => {
    // Find selected unpaid investments
    const selectedRows = pageState.data.filter((row) =>
      selectedUnpaid.includes(row.id)
    );

    // Example static company info (replace with real data as needed)
    const companyInfo = {
      name: "RoostsFundMe",
      clientNumber: "1135727096",
      routingNumber: "021000021",
    };

    // Map selected investments to payments array
    const payments = selectedRows.map((row) => ({
      recipientName: row?.user?.firstname + " " + row?.user?.lastname,
      accountNumber:
        row?.user_banking_detail?.account_number || "123456789012345",
      // Make transit number 9 digits by adding four zeros at the end if it's 5 digits
      routingNumber: row?.user_banking_detail?.transit_number
        ? row.user_banking_detail.transit_number.length === 5
          ? row.user_banking_detail.transit_number + "0000"
          : row.user_banking_detail.transit_number
        : "021000021",
      amount: row?.amount,
      currency: row?.currency,
      country: "CA",
      description:
        "Investent for " + `${row?.campaign_detail?.campaign_name}` ||
        "Investment Payment",
      customerNumber: row?.user_id
        ? row.user_id.toString().padStart(8, "0")
        : "",
    }));

    // Use today's date as dueDate (format: YYYY-MM-DD)
    const dueDate = "2025-08-06";

    // Example description
    const description = "Investment ACH Batch";

    // Get token securely from localStorage
    const token = localStorage.getItem("accessToken");

    try {
      const res = await axios.post(
        "https://dev-ach.rootsfundme.ca/api/ach/generate",
        {
          format: achFormat,
          companyInfo,
          payments,
          dueDate,
          description,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      Swal.fire({
        title: "ACH File Generated",
        text: res.data?.message || "ACH file generated successfully.",
        icon: "success",
        confirmButtonText: "OK",
      });

      // If API returns a file URL or blob, handle accordingly
      if (res.data?.data?.downloadUrl) {
        setAchFileUrl(res?.data?.data?.downloadUrl);
        setShowValidationOptions(true);
      } else if (res.data?.file) {
        // If API returns file as base64 or blob, create a URL
        const blob = new Blob([res.data.file], { type: "text/plain" });
        const blobUrl = URL.createObjectURL(blob);
        setAchFileUrl(blobUrl);
        setShowValidationOptions(true);
      } else {
        setShowValidationOptions(true);
      }
    } catch (error) {
      Swal.fire({
        title: "ACH Generation Failed",
        text: error?.response?.data?.message || error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  // Handle validation API call
  const handleValidateACH = async (file) => {
    if (!file) {
      Swal.fire({
        title: "No File Selected",
        text: "Please select or upload a file to validate.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }
    const formData = new FormData();
    formData.append("achFile", file);
    formData.append("format", achFormat);

    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.post(
        "https://dev-ach.rootsfundme.ca/api/ach/validate",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      Swal.fire({
        title: "ACH File Validated",
        text: res.data?.message || "ACH file validated successfully.",
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      Swal.fire({
        title: "Validation Failed",
        text: error?.response?.data?.message || error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    setSelectedValidationOption("upload");
    handleValidateACH(file);
  };

  const userColumns = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "user_id",
      headerName: "User ID",
      width: 70,
    },

    {
      field: "user.firstname",
      headerName: "Name",
      width: 170,
      renderCell: (params) => {
        return (
          <div className="rowitem">
            {params.row.user.firstname + " " + params.row.user.lastname}
          </div>
        );
      },
    },

    {
      field: "campaign_detail.campaign_name",
      headerName: "Campaign Name",
      width: 200,
      renderCell: (params) => {
        return (
          <div className="rowitem">
            {params?.row?.campaign_detail?.campaign_name}
          </div>
        );
      },
    },
    {
      field: "campaign_detail.corporation_legal_name",
      headerName: "Corporate Name",
      width: 200,
      renderCell: (params) => {
        return (
          <div className="rowitem">
            {params?.row?.campaign_detail?.corporation_legal_name}
          </div>
        );
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 170,
    },
    {
      field: "currency",
      headerName: "Currency",
      width: 170,
    },
    {
      field: "paymentStatus",
      headerName: "Payment Status",
      width: 170,
    },
    {
      field: "paymentOption",
      headerName: "Payment Method",
      width: 170,
    },
    {
      field: "suitabilityStatus",
      headerName: "Suitability Status",
      width: 170,
      renderCell: (params) => {
        const { isSuitabilityRejected, is_suitability_accepted } = params.row;
        let status = "PENDING";
        let statusColor = "#ff9800"; // Orange for pending
        
        if (isSuitabilityRejected === 1) {
          status = "REJECTED";
          statusColor = "#f44336"; // Red for rejected
        } else if (is_suitability_accepted === 1) {
          status = "APPROVED";
          statusColor = "#4caf50"; // Green for approved
        }
        
        return (
          <div className="rowitem">
            <span 
              style={{ 
                color: statusColor, 
                fontWeight: "bold",
                padding: "4px 8px",
                borderRadius: "4px",
                backgroundColor: statusColor + "20",
                fontSize: "12px"
              }}
            >
              {status}
            </span>
          </div>
        );
      },
    },
    {
      field: "user.profilePic",
      headerName: "Datetime",
      width: 200,
      renderCell: (params) => {
        const d = new Date(params?.row?.createdAt);
        return (
          <div className="cellWrapper ">
            <div className="profilePic">
              {d?.getHours() + ":" + d?.getMinutes() + ", " + d?.toDateString()}
            </div>
          </div>
        );
      },
    },
  ];

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      width: 200,
      renderCell: (params) => {
        return (
          <>
            <div className="cellAction">
              <Link
                to={`/investment/${params.row.id} `}
                style={{ textDecoration: "none" }}
              >
                <div className="viewButton">
                  <RemoveRedEyeOutlinedIcon />
                </div>
              </Link>
            </div>
            {params.row.paymentStatus === "UNPAID" &&
              params.row.is_suitability_accepted == 1 && (
                <div className="cellAction">
                  <Link
                    to={`/investment/edit/${params.row.id}/${params.row.campaign_detail.id}`}
                    style={{ textDecoration: "none" }}
                  >
                    <div className="editButton">
                      <EditOutlinedIcon />
                    </div>
                  </Link>
                </div>
              )}
            {params.row.is_suitability_accepted == 0 && (
              <div className="cellAction">
                <Link
                  to={`/suitability-overview/${params.row.id}`}
                  style={{ textDecoration: "none" }}
                >
                  <div className="editButton">
                    <PendingActionsIcon />
                  </div>
                </Link>
              </div>
            )}
          </>
        );
      },
    },
  ];

  return (
    <div className="datatable">
      <Loader isLoading={loading} />
      <div className="datatableTitle">
        <div className="tablesearch">
          <input
            type="text"
            placeholder="Search Campaign..."
            onChange={handleQuery}
          />
          <SearchOutlinedIcon className="sarchicon" />
        </div>
        <h5 style={{ marginLeft: "-10%" }}>Investment</h5>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            disabled={selectedUnpaid.length === 0}
            onClick={handleGenerateACH}
          >
            Generate ACH File
          </Button>
          {/* Custom dropdown for ACH Format */}
          <div style={{ minWidth: 140 }}>
            <label
              htmlFor="ach-format"
              style={{ fontSize: 14, marginRight: 8 }}
            >
              ACH Format
            </label>
            <select
              id="ach-format"
              value={achFormat}
              onChange={(e) => setAchFormat(e.target.value)}
              style={{
                padding: "6px 12px",
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: 14,
                minWidth: 120,
              }}
            >
              <option value="RBC_ACH">RBC_ACH</option>
              <option value="RBC_STANDARD">RBC_STANDARD</option>
              <option value="CPA_005">CPA_005</option>
            </select>
          </div>
        </div>
      </div>
      {/* File Download & Validation Section */}
      {showValidationOptions && (
        <div
          style={{
            margin: "24px 0",
            padding: 16,
            border: "1px solid #eee",
            borderRadius: 8,
            background: "#fafafa",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            color="success"
            href={achFileUrl}
            download="ach-file.txt"
            disabled={!achFileUrl}
            style={{ minWidth: 180 }}
          >
            Download ACH File
          </Button>
          <span style={{ fontWeight: 600, color: "#555", marginRight: 4 }}>
            Validate ACH File
          </span>
          <Button
            variant={
              selectedValidationOption === "upload" ? "contained" : "outlined"
            }
            component="label"
            color="secondary"
            style={{ minWidth: 220 }}
          >
            Upload From Computer
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.ach"
              hidden
              onChange={handleFileUpload}
            />
          </Button>
        </div>
      )}
      <DataGrid
        className="datagrid"
        autoHeight
        rows={pageState.data}
        rowCount={pageState.total}
        rowsPerPageOptions={[10, 30, 50, 70, 100]}
        pagination
        page={pageState.page - 1}
        pageSize={pageState.pageSize}
        paginationMode="server"
        onPageChange={(newPage) => {
          setPageState((old) => ({ ...old, page: newPage + 1 }));
        }}
        onPageSizeChange={(newPageSize) =>
          setPageState((old) => ({ ...old, pageSize: newPageSize }))
        }
        columns={userColumns.concat(actionColumn)}
        checkboxSelection
        isRowSelectable={(params) => params.row.paymentStatus === "UNPAID"}
        selectionModel={selectedUnpaid}
        onSelectionModelChange={(newSelection) => {
          // Only allow selection of unpaid investments
          const unpaidIds = pageState.data
            .filter((row) => row.paymentStatus === "UNPAID")
            .map((row) => row.id);
          setSelectedUnpaid(
            newSelection.filter((id) => unpaidIds.includes(id))
          );
        }}
      />
    </div>
  );
};

export default InvestmentList;
